import React, { ReactNode } from 'react';
import { PfmlEmployeeFormValues } from "../../pfml/PfmlEmployeeFormValues";
import { DocumentType } from "../../enums/DocumentType";
import { MilitaryLeaveReason } from "../../enums/MilitaryLeaveReason";
import TextInput from "../../input/TextInput";
import PhoneInput from "../../input/PhoneInput";
import { findPartyChoicesFor } from "../../enums/BenefitType";
import { Party } from "../../enums/Party";
import Document from "../common/Document";
import { ChildRelationship } from "../../enums/ChildRelationship";
import HorizontalRule from "../../structure/HorizontalRule";
import { useFormikContext } from "formik";
import { DisabilityEmployeeFormValues } from "../../disability/DisabilityEmployeeFormValues";
import { ConditionType } from "../../enums/ConditionType";
import { LeaveType } from "../../enums/LeaveType";
import { findVirtualSupplyLinkFor } from "../../../service/LinkService";
import { PdsLink } from "@principal/design-system-react";

const EmployeeDocument = () => {
    const { values } = useFormikContext<PfmlEmployeeFormValues | DisabilityEmployeeFormValues>();
    const leaveType = (values as PfmlEmployeeFormValues).leaveType;
    const conditionType = (values as DisabilityEmployeeFormValues).conditionType;
    const benefit = values.benefit;
    const biological = isBiological((values as PfmlEmployeeFormValues)?.childRelationship);
    const coveredServiceMember = (values as PfmlEmployeeFormValues)?.militaryLeaveReason === MilitaryLeaveReason.CARE_FOR_A_COVERED_SERVICE_MEMBER;
    const medicalRelated = values.medicalRelated;
    const hospitalIndemnity = values.hospitalIndemnity;
    const accident = values.accident;
    const criticalIllness = values.criticalIllness;
    const physicianStatementDocumentType = values.pfml ? DocumentType.MEDICAL_CERTIFICATION_FORM : DocumentType.PHYSICIAN_STATEMENT

    function findDefaultDocumentType() {
        if (hospitalIndemnity) {
            return DocumentType.BILLS;
        } else if ([LeaveType.SERIOUS_HEALTH_CONDITION, LeaveType.MATERNITY_LEAVE].includes(leaveType)
            || [ConditionType.SICKNESS, ConditionType.INJURY, ConditionType.PREGNANCY].includes(conditionType)) {
            return physicianStatementDocumentType;
        } else if (LeaveType.ACTIVE_DUTY_OR_MILITARY_EVENTS === leaveType) {
            return coveredServiceMember ? physicianStatementDocumentType : DocumentType.ACTIVE_DUTY_ORDERS;
        } else if (LeaveType.BONDING_WITH_A_CHILD === leaveType) {
            return biological ? DocumentType.BIRTH_CERTIFICATE : DocumentType.ADOPTION_FOSTER_PLACEMENT;
        } else if (LeaveType.PERSONAL_OR_FAMILY_VIOLENCE === leaveType) {
            return medicalRelated ? physicianStatementDocumentType : DocumentType.COPY_OF_CIVIL_COURT_APPOINTMENTS_OR_SERVICES;
        }
        return null;
    }

    let documentDescription = '' as ReactNode;
    let documentTypeOptions = [findDefaultDocumentType()] as DocumentType[];
    const supportingDocsNotice = ' If you don\'t have any supporting documentation right now, we\'ll ask you for it later via email.';
    if (hospitalIndemnity) {
        const isInjury = conditionType === ConditionType.INJURY;
        const claimsSpecificDocuments = isInjury ? <>an accident report if applicable (ex: police report)</> : <>admission/discharge paperwork</>;
        const claimsSpecificDocumentType = isInjury ? DocumentType.INCIDENT : DocumentType.ADMISSION
        documentDescription = <div>In order to review your claim for benefits, you will need to submit copies of bills, itemized receipts of services, UB-04, medical carrier's explanation of benefits, or {claimsSpecificDocuments}. <strong>This information is required.</strong></div>;
        documentTypeOptions.push(DocumentType.RECEIPTS, DocumentType.UNIFORM_BILL, DocumentType.EXPLANATION_OF_BENEFITS, claimsSpecificDocumentType);
    } else if (accident) {
        documentDescription =
            <div>In order to review your claim for benefits, you will need to submit copies of treatment notes, admit and discharge summaries or
                an <PdsLink href={findVirtualSupplyLinkFor('GP62057')} target="_blank">Attending Physician Statement</PdsLink> along with a copy of the police report if one was filed. <strong>This
                    information is required.</strong><br/><br/> If you do not have this documentation, you can download an Attending Physician Statement, save your progress, and return to upload it later.</div>;
        documentTypeOptions.push(DocumentType.ADMISSION, DocumentType.TREATMENT_NOTES, DocumentType.POLICE_DEPARTMENT_INFO);
    } else if (criticalIllness) {
        documentDescription =
            <div>In order to review your claim for benefits, you will need to submit
                a <PdsLink href={findVirtualSupplyLinkFor('GP63406')} target="_blank">medical Certification/Attending Physician Statement.</PdsLink><strong> This
                    information is required.</strong><br/><br/> If you do not have this documentation, you can download an Attending Physician Statement, save your progress, and return to upload it later. </div>;
        documentTypeOptions.push(DocumentType.ADMISSION, DocumentType.TREATMENT_NOTES);
    } else if (leaveType === LeaveType.SERIOUS_HEALTH_CONDITION
        || leaveType === LeaveType.MATERNITY_LEAVE
        || coveredServiceMember
        || medicalRelated) {
        documentDescription = "If you want to direct your physician to an online form after you file, just click Next. We'll send you a link that you can send to them. If you would also like us to reach out to the physician for their statement, please enter your physician details."
    } else if (leaveType === LeaveType.BONDING_WITH_A_CHILD) {
        documentDescription = (biological ? 'This can be a birth certificate, or a statement from the health care provider with the child\'s birthdate.'
            : 'Please upload your child\'s adoption/foster placement paperwork.') + supportingDocsNotice;
        if(biological) {
            documentTypeOptions.push(physicianStatementDocumentType);
        }
    } else if (leaveType === LeaveType.ACTIVE_DUTY_OR_MILITARY_EVENTS) {
        documentDescription = 'This document can be a military order showing active duty or impending activation.' + supportingDocsNotice;
        documentTypeOptions.push(DocumentType.LETTER_OF_IMPENDING_ACTIVATION)
    } else if (leaveType === LeaveType.PERSONAL_OR_FAMILY_VIOLENCE && !medicalRelated) {
        documentDescription = 'This document can be a court order or other documents related to civil/criminal proceedings that involve family violence.'
            + supportingDocsNotice;
    }

    return (
        <Document documentDescription={documentDescription}
                  documentTypeOptions={documentTypeOptions}
                  panelContent={findPartyChoicesFor(benefit).includes(Party.PHYSICIAN) && medicalRelated && <>
                      <HorizontalRule/>
                      <TextInput id="physician-first-name" label="Physician's first name" required={false} maxLength={1024}/>
                      <TextInput id="physician-last-name" label="Physician's last name" required={false} maxLength={1024}/>
                      <PhoneInput id="physician-phone-number" label="Physician's phone number" required={false}/>
                  </>}/>
    )
};

function isBiological(childRelationship: string | undefined) {
    const biologicalRelationships = [ChildRelationship.BIOLOGICAL_CHILD, ChildRelationship.STEP_CHILD, ChildRelationship.SPOUSE_DOMESTIC_PARTNERS_CHILD] as string[];
    return childRelationship && biologicalRelationships.includes(childRelationship);
}

export default EmployeeDocument;